import { useCancelGeneration, useGetStatus } from "@/orval/gen"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { Page } from "@components/Page"
import {
  Anchor,
  Center,
  Stack,
  Text,
  Button,
  Progress,
  Loader,
} from "@mantine/core"
import { createFileRoute } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"

export interface SearchParams {
  name?: string
}

export const Route = createFileRoute("/app/art/schedules/$taskId")({
  component: RouteComponent,
  validateSearch: (search: SearchParams) => search,
  beforeLoad: (ctx) => ({ getTitle: () => ctx.search.name }),
})

function RouteComponent() {
  const { t } = useTranslation("app_server")
  const { taskId } = Route.useParams()
  const { name } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { mutateAsync: cancel, isPending: isCancelPending } =
    useCancelGeneration({
      mutation: { onError: showErrorNotification },
    })
  const { data: status } = useGetStatus(
    { taskId },
    {
      query: {
        refetchInterval: (query) =>
          !isCancelPending && query.state.data?.status === "Pending"
            ? 2000
            : false,
      },
    }
  )

  const handleCancel = async () => {
    await cancel({ params: { taskId } })
    navigate({ to: "/app/art/schedules" })
  }

  React.useEffect(() => {
    if (status?.status === "Completed" && status.resultUrl) {
      window.open(status.resultUrl, "_blank")
    }
  }, [status?.status, status?.resultUrl])

  return (
    <Page>
      <Center h="100%">
        <Stack>
          {(!status || status.status === "Pending") && (
            <>
              <Text>{t("Schedules_GeneratingPlan", { name })}</Text>
              {status?.percentage ? (
                <>
                  <Progress value={status?.percentage ?? 0} animated />
                  {`${status?.percentage ?? 0}%`}
                </>
              ) : (
                <Center>
                  <Loader />
                </Center>
              )}
              <Button onClick={handleCancel} loading={isCancelPending}>
                {t("Cancel")}
              </Button>
            </>
          )}
          {status?.status === "Canceled" && <Text>{t("Canceled")}</Text>}
          {(status?.status === "Failed" ||
            (status?.status === "Completed" && !status.resultUrl)) && (
            <Text>{t("Schedules_TaskFailed")}</Text>
          )}
          {status?.status === "Completed" && status.resultUrl && (
            <>
              <Text>{t("Schedules_TaskCompleted")}</Text>
              <Anchor href={status.resultUrl} target="_blank">
                {t("Schedules_ViewResult")}
              </Anchor>
            </>
          )}
          <Anchor onClick={() => navigate({ to: "/app/art/schedules" })}>
            {t("Schedules_BackToSchedules")}
          </Anchor>
        </Stack>
      </Center>
    </Page>
  )
}
